const CART_ICON_SELECTOR = '[data-testid="cartButtonIcon"]';
const FLY_SIZE = 44; // px — diameter of the flying thumbnail
// Product images are large; icons/logos are small. 80px is a safe threshold.
const MIN_PRODUCT_IMAGE_SIZE = 80;
const FLY_BOX_SHADOW = '0 4px 14px rgba(0,0,0,0.22)';
const FLY_BORDER = '2px solid rgba(255,255,255,0.85)';
const FLY_BACKGROUND = '#1b1b2e';
const FLY_ARC_HEIGHT = 70; // px — how far above the linear path the midpoint rises
const FLY_DURATION = 680; // ms
const FLY_Z_INDEX = '99999';

/**
 * Finds the product image starting from `fromEl`:
 * - First looks inside `fromEl` (works when imageEl from card-product.vue is passed)
 * - Then walks up the DOM so it also works for PDP, carousel, and cart reco cards
 *   where the image is in a sibling column or ancestor container.
 */
function findProductImage(fromEl: HTMLElement): HTMLImageElement | null {
  const direct = fromEl.querySelector<HTMLImageElement>('img');
  if (direct?.src && !direct.src.startsWith('data:')) {return direct;}

  let ancestor: HTMLElement | null = fromEl.parentElement;
  while (ancestor && ancestor !== document.body) {
    const candidates = Array.from(ancestor.querySelectorAll<HTMLImageElement>('img'));
    const found = candidates.find(
      (img) =>
        img.naturalWidth >= MIN_PRODUCT_IMAGE_SIZE &&
        img.naturalHeight >= MIN_PRODUCT_IMAGE_SIZE &&
        img.src &&
        !img.src.startsWith('data:')
    );
    if (found) {return found;}
    ancestor = ancestor.parentElement;
  }
  return null;
}

export function useFlyToCart() {
  const triggerFly = (sourceEl: HTMLElement) => {
    const cartEl = document.querySelector<HTMLElement>(CART_ICON_SELECTOR);
    if (!cartEl) {return;}

    const productImg = findProductImage(sourceEl);

    // Use the product image position as the animation origin when found
    const sourceRect = (productImg ?? sourceEl).getBoundingClientRect();
    const targetRect = cartEl.getBoundingClientRect();

    const startX = sourceRect.left + sourceRect.width / 2 - FLY_SIZE / 2;
    const startY = sourceRect.top + sourceRect.height / 2 - FLY_SIZE / 2;
    const endX = targetRect.left + targetRect.width / 2 - FLY_SIZE / 2;
    const endY = targetRect.top + targetRect.height / 2 - FLY_SIZE / 2;
    const dx = endX - startX;
    const dy = endY - startY;

    const imageSrc = productImg?.currentSrc || productImg?.src;

    const flyEl = document.createElement('div');
    Object.assign(flyEl.style, {
      position: 'fixed',
      width: `${FLY_SIZE}px`,
      height: `${FLY_SIZE}px`,
      borderRadius: '50%',
      overflow: 'hidden',
      zIndex: FLY_Z_INDEX,
      pointerEvents: 'none',
      left: `${startX}px`,
      top: `${startY}px`,
      boxShadow: FLY_BOX_SHADOW,
      border: FLY_BORDER,
      background: FLY_BACKGROUND,
    });

    if (imageSrc) {
      const img = document.createElement('img');
      img.src = imageSrc;
      img.setAttribute('alt', '');
      Object.assign(img.style, { width: '100%', height: '100%', objectFit: 'cover', display: 'block' });
      flyEl.appendChild(img);
    }

    document.body.appendChild(flyEl);

    // Parabolic arc: midpoint is 70px above the linear path so the element "throws" upward
    const animation = flyEl.animate(
      [
        { transform: 'translate(0, 0) scale(1)', opacity: 1, offset: 0 },
        { transform: `translate(${dx * 0.5}px, ${dy * 0.5 - FLY_ARC_HEIGHT}px) scale(0.65)`, opacity: 0.85, offset: 0.42 },
        { transform: `translate(${dx}px, ${dy}px) scale(0.15)`, opacity: 0, offset: 1 },
      ],
      { duration: FLY_DURATION, easing: 'ease-in', fill: 'forwards' }
    );

    animation.onfinish = () => {
      flyEl.remove();
      // Bump the cart icon to acknowledge the arrival
      cartEl.animate(
        [
          { transform: 'scale(1)' },
          { transform: 'scale(1.28)' },
          { transform: 'scale(0.9)' },
          { transform: 'scale(1)' },
        ],
        { duration: 420, easing: 'ease-out' }
      );
    };
  };

  return { triggerFly };
}
